import React from "react";
import { History } from "history";
import NumberFormat from "react-number-format";
import {
  Box,
  Combo,
  CampoEstatico,
  TipoCampoEstatico,
  Row,
  Col,
  Botao,
  Form,
  Alerta,
  TipoAlerta,
  Modal,
  TipoBotao,
} from "@intechprev/componentes-web";

import { MasterPage } from "../..";
import { NumFuncionalidade } from "../../MasterPage";
import { PlanoVinculadoEntidade, ContratoEntidade } from "../../../entidades";
import { PlanoService, EmprestimoService, FichaFinanceiraService } from "../../../services";
import { reCAPTCHA } from "../../../helpers";
import { DMN_CATEGORIA, DMN_PLANO } from "../../../domains";

export interface IParametrosSimulacao {
  valorSolicitado: number;
  valorMargemConsignavel: number;
  plano: PlanoVinculadoEntidade;
  emprestimos: Array<ContratoEntidade>;
}

interface IProps {
  history?: History;
}

interface IState {
  cdPlano: string;
  plano: PlanoVinculadoEntidade;
  listaPlanos: Array<PlanoVinculadoEntidade>;

  emprestimos: Array<ContratoEntidade>;

  valorSolicitado: number;
  valorMargemConsignavel: number;
  margemPreenchida: boolean;
  limiteMaximo: number;
  limiteUnico: number;
  dataCredito: string;
  recursoUtilizado: number;
  valorDisponivel: number;

  modalValorMargemConsignavel: boolean;
  modalNovacao: boolean;

  ativoOuAssistido: boolean;
  erro: string;
}

export class SimuladorEmprestimo extends React.Component<IProps, IState> {
  private recaptcha = new reCAPTCHA("emprestimo_simulador");

  private page = React.createRef<MasterPage>();
  private form = React.createRef<Form>();
  private alert = React.createRef<Alerta>();

  state: IState = {
    cdPlano: "",
    listaPlanos: [],
    plano: new PlanoVinculadoEntidade(),

    emprestimos: [],

    valorSolicitado: 0,
    valorMargemConsignavel: 0,
    margemPreenchida: false,

    limiteMaximo: 0,
    limiteUnico: 0,
    dataCredito: "",
    recursoUtilizado: 0,
    valorDisponivel: 0,
    ativoOuAssistido: false,
    modalValorMargemConsignavel: false,
    modalNovacao: false,
    erro: "",
  };

  componentDidMount = async () => {
    try {
      await this.page.current.alternarLoading();

      var listaPlanos = await PlanoService.Buscar();

      await this.setState({
        listaPlanos,
      });

      await this.carregarDados();

      await this.page.current.alternarLoading();
    } catch (e) {
      const erro: any = e;
      await this.setState({ erro: (erro.response ? (erro.response.data) : erro) });
    }
  };

  carregarDados = async () => {
    try {
      if (this.state.cdPlano === "") {
        await this.setState({
          cdPlano: this.state.listaPlanos[0].CD_PLANO,
        });
      }

      var plano = this.state.listaPlanos.filter((plano) => plano.CD_PLANO === this.state.cdPlano);

      var valorParticipante = 0;
      const emprestimos = await EmprestimoService.BuscarAtivos();
      if (this.state.cdPlano !== DMN_PLANO.PREVIDENCIAL_A && plano[0].CD_CATEGORIA !== DMN_CATEGORIA.ASSISTIDO) {
        var saldo = await FichaFinanceiraService.BuscarPorPlano(this.state.cdPlano);

        valorParticipante = saldo.ValorParticipante;
      }
      var { limiteUnico, dataCredito, margemConsignavel, valorDisponivel } = await EmprestimoService.BuscarLimiteUnicoDataCredito(this.state.cdPlano);

      var limiteMaximo = valorParticipante * 0.7;
      var recursoUtilizado = 0;

      if (emprestimos.length > 0) {
        var indiceUltimaPrestacao = emprestimos[0].Prestacoes.length - 1;
        recursoUtilizado = emprestimos[0].Prestacoes[indiceUltimaPrestacao].VL_SALDO_ATUAL;
      }

      var valorMargemConsignavel = 0;

      if (margemConsignavel != null) {
        valorMargemConsignavel = Number(margemConsignavel);
        await this.setState({
          margemPreenchida: true,
        });
      }

      if (plano[0].CD_CATEGORIA === DMN_CATEGORIA.ATIVO) {
        valorDisponivel = (limiteUnico > limiteMaximo ? limiteMaximo : limiteUnico);
        if (plano[0].CD_PLANO === DMN_PLANO.PREVIDENCIAL_A)
          valorDisponivel = valorDisponivel - recursoUtilizado;
      }

      await this.setState({
        plano: plano[0],
        emprestimos,
        limiteMaximo,
        limiteUnico,
        dataCredito,
        recursoUtilizado,
        valorDisponivel,
        valorMargemConsignavel,
        ativoOuAssistido: ["1", "4"].includes(plano[0].CD_CATEGORIA),
      });
    } catch (e) {
      const erro: any = e;
      await this.setState({ erro: (erro.response ? (erro.response.data) : erro) });
    }
  };

  continuar = async () => {
    try {
      await this.alert.current.limparErros();
      await this.form.current.validar();

      if (this.state.valorSolicitado <= 0) {
        await this.alert.current.adicionarErro("O valor solicitado não pode ser menor ou igual a zero.");
        return;
      }

      if (this.state.plano.CD_PLANO === DMN_PLANO.PREVIDENCIAL_A && this.state.valorSolicitado && this.state.valorMargemConsignavel) {
        if (this.state.valorSolicitado > Number(this.state.valorDisponivel.toFixed(2)))
          await this.alert.current.adicionarErro('O valor solicitado não pode ser superior ao valor "Disponível".');
      }

      if (this.state.plano.CD_PLANO === DMN_PLANO.PREVIDENCIAL_B && this.state.valorSolicitado && this.state.valorMargemConsignavel) {
        if (this.state.valorSolicitado > Number(this.state.valorDisponivel.toFixed(2)))
          await this.alert.current.adicionarErro('O valor solicitado não pode ser superior ao "Valor Bruto Disponível".');
      }

      if (this.alert.current.state.mensagem.length === 0 && this.alert.current.props.mensagem.length === 0) {
        var dados: IParametrosSimulacao = {
          valorSolicitado: this.state.valorSolicitado,
          valorMargemConsignavel: Number(this.state.valorMargemConsignavel),
          plano: this.state.plano,
          emprestimos: this.state.emprestimos,
        };

        await localStorage.setItem("emprestimo-parametros", JSON.stringify(dados));
        this.props.history.push("/simuladorEmprestimoPrestacoes");
      }
    } catch (e) {
      const erro: any = e;
      await this.setState({ erro: (erro.response ? (erro.response.data) : erro) });
    }
  };

  render() {
    return (
      <MasterPage Funcionalidade={NumFuncionalidade.EMPRESTIMOS_SIMULAÇÃO} {...this.props} ref={this.page}>
        {this.state.erro !== "" && (
          <Alerta
            tipo={TipoAlerta.danger}
            mensagem={this.state.erro}
          />
        )}

        {this.state.erro === "" && (<>
          <h3>Bem-Vindo ao Simulador de Empréstimos da PREVIRB!</h3>
          <h5>Ao final da sua simulação você poderá realizar o requerimento do seu empréstimo diretamente por aqui, de forma on-line e segura!</h5>

          <Box titulo={"Selecione um Plano para a Simulação"}>
            <Combo
              contexto={this}
              nome={"cdPlano"}
              valor={this.state.cdPlano}
              opcoes={this.state.listaPlanos}
              padrao={this.state.cdPlano}
              onChange={this.carregarDados}
              nomeMembro={"DS_PLANO"}
              valorMembro={"CD_PLANO"}
            />
          </Box>

          {this.state.plano && (
            <Box titulo={`Empréstimo ${this.state.plano.DS_PLANO}`}>
              {!this.state.ativoOuAssistido && (
                <Alerta
                  tipo={TipoAlerta.danger}
                  mensagem={"Empréstimos PREVIRB disponíveis apenas para quem é Ativo ou Assistido no plano de benefícios."}
                />
              )}

              {this.state.ativoOuAssistido && (
                <>
                  <p>
                    Clique{" "}
                    <a className={"text-secondary"} target={"_blank"} href="./regulamentoEmprestimo.pdf">
                      aqui
                    </a>{" "}
                    para visualizar o regulamento de empréstimo.
                  </p>
                  <Row className={"text-center"}>
                    {this.state.cdPlano !== "0001" && this.state.plano.CD_CATEGORIA !== "4" && (
                      <>
                        <Col>
                          <Box>
                            <h4 style={{ fontWeight: "bold" }}>Limite Máximo Bruto</h4>
                            <p style={{ fontSize: 12 }}>70% do Saldo da Conta Participante (Excluídos os recursos portados)</p>
                            <h3 className={"text-primary"}>
                              <CampoEstatico valor={this.state.limiteMaximo} tipo={TipoCampoEstatico.dinheiro} />
                            </h3>
                          </Box>
                        </Col>
                        <Col>
                          <Box>
                            <h4 style={{ fontWeight: "bold" }}>Limite Único</h4>
                            <p style={{ fontSize: 12 }}>Maior valor de empréstimo para seu plano</p>
                            <h3 className={"text-primary"}>
                              <CampoEstatico valor={this.state.limiteUnico} tipo={TipoCampoEstatico.dinheiro} />
                            </h3>
                          </Box>
                        </Col>
                      </>
                    )}
                    <Col>
                      <Box>
                        <h4 style={{ fontWeight: "bold" }}>Data de Crédito</h4>
                        <p style={{ fontSize: 12 }}>Data que você receberá o seu empréstimo</p>
                        <h3 className={"text-primary"}>
                          <CampoEstatico valor={this.state.dataCredito} />
                        </h3>
                      </Box>
                    </Col>
                    <Col>
                      <Box>
                        <h4 style={{ fontWeight: "bold" }}>Recurso Já Utilizado</h4>
                        <p style={{ fontSize: 12 }}>
                          Saldo de empréstimos ativos
                          <br />
                          <br />
                        </p>
                        <h3 className={"text-secondary"}>
                          <CampoEstatico valor={this.state.recursoUtilizado} tipo={TipoCampoEstatico.dinheiro} />
                        </h3>
                      </Box>
                    </Col>
                    <Col>
                      <Box>
                        <h4 style={{ fontWeight: "bold" }}>{this.state.plano.CD_PLANO === DMN_PLANO.PREVIDENCIAL_A ? "Disponível" : "Valor Bruto Disponível"}</h4>
                        <p style={{ fontSize: 12 }}>
                          Valor que você pode requerer
                          <br />
                          <br />
                        </p>
                        <h3 className={"text-success"}>
                          <CampoEstatico valor={this.state.valorDisponivel} tipo={TipoCampoEstatico.dinheiro} />
                        </h3>
                      </Box>
                    </Col>
                  </Row>

                  <Box titulo={"Parâmetros para Simulação"}>
                    <Form ref={this.form}>
                      <Row formGroup>
                        <Col className="col-lg-12">
                          <b>
                            <label htmlFor="valorSolicitado">{this.state.plano.CD_PLANO === DMN_PLANO.PREVIDENCIAL_A ? "Valor Desejado de Empréstimo *" : "Valor desejado do empréstimo (restrito ao campo \"Valor Bruto Disponível\") *"}</label>
                          </b>
                        </Col>

                        <Col className="col-lg-12">
                          <NumberFormat
                            name={"valorSolicitado"}
                            id={"valorSolicitado"}
                            className={"form-control"}
                            thousandSeparator={"."}
                            decimalSeparator=","
                            allowedDecimalSeparators={["."]}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            value={this.state.valorSolicitado}
                            onValueChange={(e) => this.setState({ valorSolicitado: Number(e.value) })}
                          />
                        </Col>
                      </Row>

                      <Row formGroup>
                        <Col className="col-lg-12">
                          <b>
                            <label htmlFor="valorSolicitado">
                              Margem Consignável *{" "}
                              <small>
                                (Valor da margem a ser confirmado pela fonte pagadora, após a data do efetivo pagamento do mês do crédito do empréstimo)
                              </small>
                            </label>
                          </b>
                        </Col>

                        <Col className="col-lg-12">
                          <div className="input-group">
                            <NumberFormat
                              name={"valorMargemConsignavel"}
                              id={"valorMargemConsignavel"}
                              className={"form-control"}
                              thousandSeparator={"."}
                              decimalSeparator=","
                              allowedDecimalSeparators={["."]}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              value={this.state.valorMargemConsignavel}
                              disabled={this.state.margemPreenchida}
                              onValueChange={(e) => this.setState({ valorMargemConsignavel: Number(e.value) })}
                            />
                            <div className="input-group-append">
                              <Botao
                                titulo="O que é isso?"
                                onClick={() =>
                                  this.setState({
                                    modalValorMargemConsignavel: !this.state.modalValorMargemConsignavel,
                                  })
                                }
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Modal
                        onClose={() =>
                          this.setState({
                            modalValorMargemConsignavel: !this.state.modalValorMargemConsignavel,
                          })
                        }
                        onConfirm={() =>
                          this.setState({
                            modalValorMargemConsignavel: !this.state.modalValorMargemConsignavel,
                          })
                        }
                        visivel={this.state.modalValorMargemConsignavel}
                        titulo={"O que é a Margem Consignável?"}
                      >
                        Margem Consignável é o valor máximo da renda de um trabalhador, aposentado, pensionista ou servidor público que pode ser
                        comprometido do seu salário ou benefício mensal em um empréstimo consignado, no qual o valor do empréstimo é descontado
                        diretamente da folha de pagamento, contracheque ou conta de benefício INSS.
                        <br />
                        <br />
                        Você encontrará margem consignável no seu contracheque.
                      </Modal>

                      <Modal
                        onClose={() =>
                          this.setState({
                            modalNovacao: !this.state.modalNovacao,
                          })
                        }
                        onConfirm={() =>
                          this.setState({
                            modalNovacao: !this.state.modalNovacao,
                          })
                        }
                        visivel={this.state.modalNovacao}
                        titulo={"O que é a Novação de Dívida?"}
                      >
                        Novação é a transformação de uma dívida em outra, com extinção da antiga. Desta forma surge uma nova dívida do devedor em
                        relação ao credor, com o desaparecimento da original.
                        <br />
                        <br />
                        Dá-se a novação:
                        <br />
                        <br />
                        <ul>
                          <li>Quando o devedor contrai com o credor nova dívida para extinguir e substituir a anterior;</li>
                          <li>Quando novo devedor sucede ao antigo, ficando este quite com o credor;</li>
                          <li>Quando, em virtude de obrigação nova, outro credor é substituído ao antigo, ficando o devedor quite com este.</li>
                        </ul>
                      </Modal>

                      {this.state.emprestimos.length > 0 && (
                        <>
                          <p className={"mt-5"}>
                            Você já possúi {this.state.emprestimos.length} contratos ativos. Esse contrato será reformado - Novação
                            <Botao
                              tipo={TipoBotao.link}
                              titulo={"(O que é isso?)"}
                              onClick={() => {
                                this.setState({
                                  modalNovacao: !this.state.modalNovacao,
                                });
                              }}
                            />
                            {/* <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                this.setState({
                                  modalNovacao: !this.state.modalNovacao,
                                });
                              }}
                            >
                              O que é isso?
                            </a> */}
                          </p>
                          <table className={"table"}>
                            <thead>
                              <tr>
                                <th>Nº/Ano</th>
                                <th>Crédito</th>
                                <th>Valor Solicitado</th>
                                <th>Saldo Devedor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {this.state.emprestimos.map((info: any, index: number) => {
                                return (
                                  <tr key={index}>
                                    <td>{`${info.NUM_CONTRATO}/${info.ANO_CONTRATO}`}</td>
                                    <td>{info.DT_CREDITO}</td>
                                    <td>
                                      <CampoEstatico valor={info.VL_SOLICITADO} tipo={TipoCampoEstatico.dinheiro} />
                                    </td>
                                    <td>
                                      <CampoEstatico
                                        valor={info.Prestacoes[info.Prestacoes.length - 1].VL_SALDO_ATUAL}
                                        tipo={TipoCampoEstatico.dinheiro}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </>
                      )}

                      <Botao titulo={"Calcular Prazos e Prestações"} onClick={this.continuar} submit />
                      <br />
                      <br />
                      <Alerta ref={this.alert} padraoFormulario tipo={TipoAlerta.danger} tamanho={"6"} />
                    </Form>
                  </Box>
                </>
              )}
            </Box>
          )}
        </>)}
      </MasterPage>
    );
  }
}
